import { useState } from 'react';
import { Modal } from 'bootstrap';
import { uniqueNamesGenerator, Config, adjectives, animals } from 'unique-names-generator';
import { createButton, updateButton } from '../services/api';
import { CustomButton } from '../types';
import { useToast } from '../utils/toastUtils';
import { useUser } from '../contexts/UserContext';

// Common popular icons
export const popularIcons = [
  'bi bi-star',         // Star
  'bi bi-heart',        // Heart
  'bi bi-trophy',       // Trophy
  'bi bi-lightning',    // Lightning
  'bi bi-emoji-smile',  // Smile
  'bi bi-fire',         // Fire
  'bi bi-hand-thumbs-up', // Thumbs up
  'bi bi-gem',          // Gem
  'bi bi-stars'         // Stars
];

// Common popular colors
export const popularColors = [
  '#212529', // Charcoal black
  '#5F0F40', // Deep burgundy  
  '#F94144', // Vermilion red
  '#F15BB5', // Hot pink
  '#7B2CBF', // Violet purple
  '#2D46B9', // Royal blue
  '#009B72', // Emerald green
  '#48BF84', // Mint green
  '#FFBE0B'  // Golden yellow
];

interface UseButtonFormOptions {
  counterId: string;
  modalId?: string;
  onSuccess?: () => Promise<void>;
  isOwnerUrl?: boolean;
}

/**
 * Custom hook for managing button creation and editing
 */
export function useButtonForm({ counterId, modalId = 'buttonModal', onSuccess, isOwnerUrl = false }: UseButtonFormOptions) {
  const { showToast } = useToast();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingButton, setEditingButton] = useState<CustomButton | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#0d6efd',
    icon: 'bi bi-star',
    costToClick: undefined as number | undefined
  });

  // Get login state from UserContext
  const { isLoggedIn } = useUser();

  /**
   * Generate a random button configuration
   */
  const generateRandomButtonConfig = () => {
    // Generate random button name
    const nameConfig: Config = {
      dictionaries: [adjectives, animals],
      separator: ' ',
      style: 'capital'
    };
    
    const randomName = uniqueNamesGenerator(nameConfig);
    
    // Select a random color from popularColors
    const randomColor = popularColors[Math.floor(Math.random() * popularColors.length)];
    
    // Select a random icon from popularIcons
    const randomIcon = popularIcons[Math.floor(Math.random() * popularIcons.length)];
    
    return { name: randomName, color: randomColor, icon: randomIcon, costToClick: undefined as number | undefined };
  };

  /**
   * Handle form field changes
   */
  const handleFormChange = (field: string, value: string | number) => {
    // Special handling for costToClick
    if (field === 'costToClick') {
      if (value === '' || value === null) {
        setFormData({ ...formData, costToClick: undefined });
      } else {
        // Ensure numeric value
        const numericValue = typeof value === 'string' ? parseFloat(value) : value;
        setFormData({ ...formData, costToClick: numericValue });
      }
    } else {
      // Normal handling for other fields
      setFormData({ ...formData, [field]: value });
    }
  };

  /**
   * Handle adding a new button
   */
  const handleAddNew = () => {
    setIsEditMode(false);
    setEditingButton(null);
    
    const randomConfig = generateRandomButtonConfig();
    setFormData(randomConfig);
    
    const modal = new Modal(document.getElementById(modalId)!);
    modal.show();
  };

  /**
   * Handle editing an existing button
   */
  const handleEdit = (button: CustomButton) => {
    setEditingButton(button);
    setIsEditMode(true);
    
    // Ensure we capture all button properties, including costToClick
    // which might be undefined, null, or have a numeric value
    const costToClickValue = button.costToClick !== undefined ? button.costToClick : undefined;
    
    setFormData({
      name: button.name,
      color: button.color,
      icon: button.icon,
      costToClick: costToClickValue
    });
    
    const modal = new Modal(document.getElementById(modalId)!);
    modal.show();
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!counterId) return;
    
    try {
      if (isEditMode && editingButton) {
        const updatedButton = await updateButton(counterId, editingButton._id, formData, isOwnerUrl);
        showToast('Button updated successfully', 'success');
      } else {
        const newButton = await createButton(counterId, formData, isOwnerUrl);
        showToast('Button created successfully', 'success');
      }

      const modal = Modal.getInstance(document.getElementById(modalId)!);
      modal?.hide();
      
      // If an onSuccess callback is provided, call it
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error('Error saving button:', error);
      showToast('Failed to save button', 'danger');
    }
  };

  return {
    formData,
    isEditMode,
    handleFormChange,
    handleAddNew,
    handleEdit,
    handleSubmit,
    popularIcons,
    popularColors,
    isLoggedIn
  };
} 